import { fitTitle, fitDescription } from "@/lib/seo-text";
import { Link, createFileRoute } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import { FIRM, faqSchema } from "@/lib/firm";

const STEPS = [
  {
    t: "1. Tell us who holds the will",
    d: "The firm's name is usually enough. If the firm has merged or closed, tell us the old name and we will trace who took the safe custody packet.",
  },
  {
    t: "2. We send you a one-page authority",
    d: "You sign a short written authority directing the holder to release the original will and any enduring power of attorney to us. It is emailed to you and signed electronically.",
  },
  {
    t: "3. We write and collect it",
    d: "We deal with the other firm, the bank or the Public Trustee of Queensland directly. You never have to explain yourself to them or ask for a favour.",
  },
  {
    t: "4. It goes into our safe custody, free",
    d: "You get a written receipt and a register entry so your executor can find it. Storage is free, and there is no charge for the transfer itself.",
  },
];

const FAQS = [
  {
    q: "Can I move my will to another solicitor?",
    a: "Yes. The original will is your document. A firm holding it in safe custody must release it on your written authority, and it cannot be held back because of an old account or because they prepared it.",
  },
  {
    q: "Do I have to tell my old solicitor why?",
    a: "No. You give a written direction, we send it, and the will is released. No explanation is required and we do not ask you for one.",
  },
  {
    q: "Does it cost anything to transfer a will?",
    a: "Not with us. There is no charge to request the will, take it into safe custody or store it. A firm releasing it may not charge for the release either, though some ask for a small handling or postage cost.",
  },
  {
    q: "Can my executor do this after I die?",
    a: "Yes. On proof of death and identity, the executor named in the will can require the holder to release the original. We do that as part of our fixed-fee probate work.",
  },
  {
    q: "What if the firm that made the will no longer exists?",
    a: "Safe custody packets almost always transfer to a successor practice when a firm closes or merges, and the Queensland Law Society can help identify who holds them. Banks and the Public Trustee of Queensland are the other common holders.",
  },
  {
    q: "Do I need a new will, or just the old one moved?",
    a: "Moving it is a separate question from whether it still works. Once we hold it we will read it and tell you plainly whether it still matches your circumstances — that review is free and there is no obligation to have us redraft anything.",
  },
];

const TITLE = `Move Your Will to Us | Free Safe Custody Transfer — ${FIRM.name}`;
const DESCRIPTION =
  "Transfer your original will from another Queensland law firm, bank or the Public Trustee. We write for its release on your authority, store it free in safe custody, and review it at no charge.";

export const Route = createFileRoute("/transfer-my-will-from-another-firm")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/transfer-my-will-from-another-firm" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/transfer-my-will-from-another-firm" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: TransferWillPage,
});

function TransferWillPage() {
  return (
    <>
      <PageHero
        eyebrow="Safe custody transfer"
        title="Move your will to us — we do the asking"
        lead="If another firm, a bank or the Public Trustee holds your original will, you can move it whenever you like. Tell us who has it and we write for it on your authority. Free to transfer, free to store, and we read it and tell you whether it still works."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { t: "No charge", d: "No fee to request the will, transfer it or store it in safe custody" },
            { t: "No awkward call", d: "You never speak to the firm holding it — we do" },
            { t: "Free review", d: "We read it and tell you plainly whether it still matches your life" },
          ].map((c) => (
            <div key={c.t} className="rounded-sm border border-border bg-card p-6">
              <p className="font-serif text-lg text-primary">{c.t}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">How the transfer works</h2>
        <ol className="mt-6 space-y-5">
          {STEPS.map((s) => (
            <li key={s.t} className="rounded-sm border border-border bg-secondary/40 p-6">
              <p className="font-serif text-xl text-foreground">{s.t}</p>
              <p className="measure mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </li>
          ))}
        </ol>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Why people move their will</h2>
        <div className="measure mt-5 space-y-5 text-base leading-relaxed text-muted-foreground">
          <p>
            Usually because the firm that drew it has been absorbed into a larger practice, the
            solicitor who knew them has retired, or nobody can tell them who holds it any more. A
            will that cannot be found is a real problem: the Supreme Court presumes a missing will
            was destroyed and revoked, and rebutting that presumption is slower and more expensive
            than the original will ever was.
          </p>
          <p>
            Others move it because they want the person storing the will to be the person who will
            act for the executor. When the two are the same firm, the estate starts the day after
            the funeral rather than three weeks later.
          </p>
          <p>
            You do not need a reason and you do not need permission. Written authority from you is
            enough.
          </p>
        </div>

        <div className="mt-14">
          <EnquiryForm heading="Tell us who holds your will" />
        </div>

        <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
          Include the name of the firm, bank or trustee holding it in the message field — that is
          all we need to get started. Executors dealing with an estate should use the{" "}
          <Link to="/executor-intake" className="font-semibold text-primary underline">
            executor intake
          </Link>{" "}
          instead, and{" "}
          <Link to="/will-safe-custody" className="font-semibold text-primary underline">
            our guide to finding a will in safe custody
          </Link>{" "}
          covers what to do when nobody knows where it is.
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>
      </section>

      <CtaBand
        title="Not sure who has your will?"
        body={`Call ${FIRM.phone} and we'll work through where to look — solicitor, bank, accountant or the Public Trustee.`}
      />
    </>
  );
}
